import type { MarketedMembershipPlan } from '@frontend/api-client';
import { getMembershipPlanDisplayPricing } from '@frontend/api-client';
import type { PromotionDetails } from '@frontend/api-types';
import { InlineAlert, Text, YStack } from '@frontend/web-react';
import { PaymentCapture, type PaymentCaptureRef } from '../../../PaymentCapture';
import { SubscriptionSummary } from './SubscriptionSummary';

export type PaymentCaptureStepProps = {
  amountCharged: Cent;
  nextBillingDate: DateISOString;
  onError: (error: string) => void;
  onSuccess: (data: { amountCharged: Cent; nextBillingDate: DateISOString }) => void;
  onboardingSessionId?: string;
  paymentCaptureRef: React.RefObject<PaymentCaptureRef | null>;
  paymentError?: string;
  planCode: string;
  promoCode?: string;
  promotionDescription?: string;
  promotionDetails?: PromotionDetails;
  promotionName?: string;
  selectedPlanData: MarketedMembershipPlan;
  step: 'paymentCapture';
};

export const PaymentCaptureStep = ({
  amountCharged,
  nextBillingDate,
  onboardingSessionId,
  onError,
  onSuccess,
  paymentCaptureRef,
  paymentError,
  planCode,
  promoCode,
  promotionDescription,
  promotionDetails,
  promotionName,
  selectedPlanData,
  step,
}: PaymentCaptureStepProps) => {
  const selectedPlanPricing = getMembershipPlanDisplayPricing(selectedPlanData);

  return (
    <YStack gapY="lg">
      <SubscriptionSummary
        nextBillingDate={nextBillingDate}
        promoCode={promoCode}
        promotionDescription={promotionDescription}
        promotionDetails={promotionDetails}
        promotionName={promotionName}
        selectedPlanData={selectedPlanData}
      />

      <YStack gapY="lg" pt="lg">
        <YStack>
          <Text type="h3">Add a payment method</Text>
          <Text color="tertiary" type="p-sm">
            We will use this to charge your monthly subscription.
          </Text>
        </YStack>
        {paymentError ? <InlineAlert description={paymentError} variant="error" /> : null}

        <PaymentCapture
          amountCharged={amountCharged}
          nextBillingDate={nextBillingDate}
          onError={onError}
          onSuccess={onSuccess}
          onboardingSessionId={onboardingSessionId}
          planCode={planCode}
          planPrice={selectedPlanPricing?.primaryPrice ?? selectedPlanData.price}
          promoCode={promoCode}
          ref={paymentCaptureRef}
          step={step}
        />
      </YStack>
    </YStack>
  );
};
